import ConfidenceBadge from "@/components/ConfidenceBadge";
import { Section, SectionHead } from "@/components/common";

const SCALE = [
  ["verified", "Independently verified against primary records or audited data."],
  ["reported", "Reported by a named, credible source (government, utility, operator, peer-reviewed study)."],
  ["estimated", "An estimate with a stated method; not a measured value."],
  ["modeled", "Produced by a model or projection; depends on assumptions."],
  ["unknown", "No reliable public figure exists. We show 'Not publicly reported' rather than guess."],
];

export default function Methodology() {
  return (
    <Section className="py-12 sm:py-16 max-w-4xl">
      <SectionHead eyebrow="Methodology & Sources" title="How we research and what we won't do"
        desc="Trust is the product. Every factual statistic carries a visible source link, a publication date, and a last-updated date." />

      <div className="card p-6 sm:p-8 space-y-8">
        <div>
          <h3 className="font-display font-bold text-xl text-white mb-4">Confidence scale</h3>
          <div className="space-y-3">
            {SCALE.map(([lvl, desc]) => (
              <div key={lvl} className="flex items-start gap-3" data-testid={`scale-${lvl}`}>
                <div className="shrink-0 w-44"><ConfidenceBadge level={lvl} /></div>
                <p className="text-slate-300 text-sm">{desc}</p>
              </div>
            ))}
          </div>
        </div>

        <div>
          <h3 className="font-display font-bold text-xl text-white mb-3">Counting rules</h3>
          <ul className="space-y-2 text-slate-300 text-sm list-disc pl-5">
            <li>Facility counts vary by whether a source counts individual buildings, campuses, or companies. We state the counting method.</li>
            <li>Proposed projects are never combined with operating facilities. Operating, under-construction, proposed, and closed/canceled are kept separate.</li>
            <li>We never invent or estimate facility totals without a cited methodology.</li>
            <li>Water figures distinguish withdrawn vs. consumed, with units, time period, location, and measurement boundary.</li>
          </ul>
        </div>

        <div>
          <h3 className="font-display font-bold text-xl text-white mb-3">What we will not do</h3>
          <ul className="space-y-2 text-slate-300 text-sm list-disc pl-5">
            <li>No fake citations, fake project records, placeholder statistics, or unsupported claims.</li>
            <li>We don't bypass logins, paywalls, or technical restrictions, and we don't republish full articles or large excerpts.</li>
            <li>If a source is unavailable or a fact can't be verified, we say so rather than fill the gap with invented information.</li>
            <li>Advertising and sponsorship never change research findings, source selection, project counts, rankings, or editorial conclusions.</li>
          </ul>
        </div>

        <div>
          <h3 className="font-display font-bold text-xl text-white mb-3">AI summaries</h3>
          <p className="text-slate-300 text-sm">News summaries and "Why this matters" notes may be AI-generated from source metadata. These are clearly labeled "AI summary" and always link to the original source. AI is never used to invent facts.</p>
        </div>

        <div>
          <h3 className="font-display font-bold text-xl text-white mb-3">Disclosure</h3>
          <p className="text-slate-300 text-sm">DYORUSA discloses relevant ownership, sponsorship, and business relationships. Core public education and basic research remain free.</p>
        </div>
      </div>
    </Section>
  );
}
